import Phaser from 'phaser';
import { sound } from '../../services/audioService.js';
import { ensureTextures } from '../textureGenerator.js';

export class SleepGame extends Phaser.Scene {
  constructor() {
    super({ key: 'SleepGame' });
  }

  init(data) {
    this.difficulty = data.difficulty || 'medium';
    this.barsToAdd = data.barsToAdd || (this.difficulty === 'hard' ? 9 : (this.difficulty === 'medium' ? 6 : 3));
    this.onComplete = data.onComplete;
    this.score = 0;
    this.isGameOver = false;

    if (this.difficulty === 'easy') {
      this.targetScore = 8;
      this.scrollSpeed = 100;
      this.cloudGapMin = 130;
      this.cloudGapMax = 180;
    } else if (this.difficulty === 'medium') {
      this.targetScore = 12;
      this.scrollSpeed = 140;
      this.cloudGapMin = 160;
      this.cloudGapMax = 220;
    } else {
      this.targetScore = 16;
      this.scrollSpeed = 180;
      this.cloudGapMin = 180;
      this.cloudGapMax = 260;
    }
  }

  create() {
    ensureTextures(this);
    const { width, height } = this.cameras.main;

    // Dreamy Night Sky Background
    this.add.rectangle(width / 2, height / 2, width, height, 0x0E0B20);

    // Stars in background
    for (let i = 0; i < 40; i++) {
      const sx = Phaser.Math.Between(0, width);
      const sy = Phaser.Math.Between(0, height);
      const sz = Phaser.Math.FloatBetween(1, 2.5);
      const star = this.add.circle(sx, sy, sz, 0xFFFFFF, Phaser.Math.FloatBetween(0.3, 0.9));
      this.tweens.add({
        targets: star,
        alpha: 0.2,
        duration: Phaser.Math.Between(800, 2000),
        yoyo: true,
        repeat: -1
      });
    }

    // Platforms & Collectibles Groups
    this.clouds = this.physics.add.group();
    this.stars = this.physics.add.group();

    // Initial Starting Platform
    const startCloud = this.clouds.create(width / 2, height - 100, 'cloud_plat');
    startCloud.body.setImmovable(true);
    startCloud.body.allowGravity = false;

    // Player Dream Jumper
    this.player = this.physics.add.sprite(width / 2, height - 160, 'particle_star');
    this.player.setScale(1.2);
    this.player.setTint(0xC77DFF);
    this.player.body.setGravityY(750);
    this.player.setCollideWorldBounds(false);

    // Collision
    this.physics.add.collider(this.player, this.clouds, () => {
      this.canJump = true;
    });

    // Star collection
    this.physics.add.overlap(this.player, this.stars, (player, star) => {
      star.destroy();
      sound.playCatch();
      this.score++;
      this.scoreText.setText(`Dream Stars: ${this.score}/${this.targetScore}`);
      if (this.score >= this.targetScore) {
        this.triggerEnd(true);
      }
    });

    // Tap to Jump
    this.canJump = true;
    this.input.on('pointerdown', () => {
      if (this.isGameOver) return;
      if (this.canJump) {
        sound.playJump();
        this.player.setVelocityY(-460);
        this.player.setVelocityX(Phaser.Math.Between(-40, 40));
        this.canJump = false;
      }
    });

    // HUD
    this.scoreText = this.add.text(16, 16, `Dream Stars: ${this.score}/${this.targetScore}`, {
      fontSize: '15px',
      fontStyle: 'bold',
      color: '#C77DFF'
    });

    this.hintText = this.add.text(width / 2, height - 30, 'Tap screen to hop across clouds ✨', {
      fontSize: '11px',
      color: 'rgba(255,255,255,0.5)'
    }).setOrigin(0.5);

    // Spawn Clouds ahead
    this.lastSpawnY = height - 100;
    for (let i = 0; i < 6; i++) {
      this.spawnNextCloud();
    }
  }

  spawnNextCloud() {
    const { width } = this.cameras.main;
    const nextY = this.lastSpawnY - Phaser.Math.Between(this.cloudGapMin, this.cloudGapMax);
    const nextX = Phaser.Math.Between(60, width - 60);

    const cloud = this.clouds.create(nextX, nextY, 'cloud_plat');
    cloud.body.setImmovable(true);
    cloud.body.allowGravity = false;

    // Moving cloud variation on medium/hard
    if (this.difficulty !== 'easy' && Math.random() < 0.5) {
      this.tweens.add({
        targets: cloud,
        x: nextX > width / 2 ? nextX - 80 : nextX + 80,
        duration: Phaser.Math.Between(1500, 2500),
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut'
      });
    }

    // Spawn a shiny star above cloud
    const star = this.stars.create(nextX, nextY - 35, 'particle_star');
    star.setScale(0.8);
    star.setTint(0xFFD700);
    star.body.allowGravity = false;

    this.lastSpawnY = nextY;
  }

  update() {
    if (this.isGameOver) return;
    const { width, height } = this.cameras.main;

    // Camera follow player upward
    if (this.player.y < height / 2) {
      const diff = height / 2 - this.player.y;
      this.player.y = height / 2;

      this.clouds.getChildren().forEach((c) => {
        c.y += diff;
      });
      this.stars.getChildren().forEach((s) => {
        s.y += diff;
      });
      this.lastSpawnY += diff;
    }

    // Spawn more clouds as camera ascends
    if (this.lastSpawnY > 0) {
      this.spawnNextCloud();
    }

    // Player fell down
    if (this.player.y > height + 50) {
      sound.playHit();
      this.triggerEnd(false);
    }
  }

  triggerEnd(isWin) {
    this.isGameOver = true;
    const { width, height } = this.cameras.main;
    this.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0.8);

    if (isWin) {
      sound.playVictory();
      this.add.text(width / 2, height / 2 - 50, '🌙 SWEET DREAMS!', {
        fontSize: '24px',
        fontStyle: '800',
        color: '#00E676'
      }).setOrigin(0.5);

      this.add.text(width / 2, height / 2 - 10, `+${this.barsToAdd} SLEEP BARS GAINED! 💤`, {
        fontSize: '15px',
        fontStyle: 'bold',
        color: '#00E676'
      }).setOrigin(0.5);

      const finishBtn = this.add.rectangle(width / 2, height / 2 + 50, 140, 42, 0x00E676)
        .setInteractive({ cursor: 'pointer' });
      this.add.text(width / 2, height / 2 + 50, 'CLAIM & EXIT', {
        fontSize: '14px',
        fontStyle: 'bold',
        color: '#000'
      }).setOrigin(0.5);

      finishBtn.on('pointerdown', () => {
        sound.playClick();
        if (this.onComplete) this.onComplete(true);
      });
    } else {
      sound.playGameOver();
      this.add.text(width / 2, height / 2 - 40, '☁️ FELL OUT OF DREAM', {
        fontSize: '20px',
        fontStyle: '800',
        color: '#FF5252'
      }).setOrigin(0.5);

      this.add.text(width / 2, height / 2 - 5, `Collected ${this.score}/${this.targetScore} stars`, {
        fontSize: '13px',
        color: '#A0A5BA'
      }).setOrigin(0.5);

      const retryBtn = this.add.rectangle(width / 2, height / 2 + 45, 120, 38, 0x9D4EDD)
        .setInteractive({ cursor: 'pointer' });
      this.add.text(width / 2, height / 2 + 45, 'RETRY', {
        fontSize: '13px',
        fontStyle: 'bold',
        color: '#FFF'
      }).setOrigin(0.5);

      retryBtn.on('pointerdown', () => {
        sound.playClick();
        this.scene.restart();
      });
    }
  }
}
